// Availability calendar: visual month grid where booked dates are greyed out.
// Click a date to set check-in, click another to set check-out. Selected range
// is highlighted. Prevents selecting booked dates or ranges that overlap them.
// Shows a DYNAMIC price estimate ("around $X for N nights, before fees") under
// the calendar AFTER the guest picks both dates, and gates direct stays to
// 6-28 nights (D19).
//
// Loading: selection is DISABLED until availability data loads (never "always
// free" while loading). A cached copy + refresh button keep it fast + reliable.
// THE INVARIANT (session 34): a failed first load is NOT "all dates free" —
// fetchAvailability throws, the calendar stays locked behind an error + retry
// state, and the failure is reported server-side (client_errors/). Selection
// only ever unlocks on a real data set (cache or network).
import { useState, useEffect, useMemo } from "preact/hooks";
import { MIN_NIGHTS, MAX_NIGHTS } from "../config.js";
import {
  fetchAvailability,
  isRangeAvailable,
  estimatePrice,
  cacheIsStale,
} from "../lib/availability.js";
import { toISODate, computeNights, inNightRange, isBeyondAdvanceWindow } from "../lib/validation.js";
import { describeError, reportError } from "../lib/errlog.js";
import { Status } from "./Status.jsx";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function parseISO(d) {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, day));
}

function toISO(d) {
  return toISODate(d);
}

function isDateBooked(date, bookedRanges) {
  const t = date.getTime();
  return bookedRanges.some((r) => {
    const b0 = parseISO(r.start).getTime();
    const b1 = parseISO(r.end).getTime();
    // [start, end): end day (checkout / guest-change day) is selectable.
    return t >= b0 && t < b1;
  });
}

function isPast(date, today) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const t = new Date(today);
  t.setHours(0, 0, 0, 0);
  return d < t;
}

function isInSelectedRange(date, checkin, checkout) {
  if (!checkin) return false;
  const t = date.getTime();
  const ci = parseISO(checkin).getTime();
  const co = checkout ? parseISO(checkout).getTime() : null;
  if (co) return t >= ci && t <= co;
  return t === ci;
}

export function AvailabilityCalendar({ onSelectDates }) {
  const [data, setData] = useState({ booked: [], pricing_band: null, rate_bands: [] });
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [hoverDate, setHoverDate] = useState("");
  const [msg, setMsg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Load (and retry): fetch serves the cache fast if present. `loaded` is set
  // ONLY by a real data set — a failure parks the calendar in the error state
  // (selection stays locked) and reports server-side so a guest's dead
  // calendar is visible in client_errors/, never silently free.
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    fetchAvailability()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        reportError("availability_load", err);
        setLoadError(describeError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  function refresh() {
    setRefreshing(true);
    fetchAvailability({ fresh: true })
      .then((d) => {
        setData(d);
        setLoaded(true);
        setMsg(null);
      })
      .catch(() => {
        setMsg({ kind: "warn", text: "Couldn't refresh availability right now. Showing the last known dates." });
      })
      .finally(() => setRefreshing(false));
  }

  const today = useMemo(() => new Date(), []);

  const nights = checkin && checkout ? computeNights(checkin, checkout) : null;
  const available = checkin && checkout ? isRangeAvailable(data.booked, checkin, checkout) : null;
  const priceEst = checkin && checkout ? estimatePrice(data, checkin, checkout) : null;
  const stale = loaded ? cacheIsStale() : false;

  function handleDateClick(date) {
    setMsg(null);
    const iso = toISO(date);
    if (isPast(date, today)) return;
    if (isBeyondAdvanceWindow(date, today)) return;
    if (isDateBooked(date, data.booked)) return;

    // If no checkin, or both checkin+checkout set, start fresh.
    if (!checkin || (checkin && checkout)) {
      setCheckin(iso);
      setCheckout("");
      return;
    }

    // We have a checkin, this click sets checkout.
    if (iso === checkin) {
      // Clicking the same date resets (reselectable / unselect).
      setCheckin("");
      setCheckout("");
      return;
    }

    const ci = parseISO(checkin);
    let newCheckin = checkin;
    let newCheckout = iso;
    if (date < ci) {
      newCheckin = iso;
      newCheckout = toISO(ci);
    }

    if (!isRangeAvailable(data.booked, newCheckin, newCheckout)) {
      setMsg({ kind: "err", text: "Those dates overlap an existing booking. Try different nights." });
      setCheckin("");
      setCheckout("");
      return;
    }

    const n = computeNights(newCheckin, newCheckout);
    if (n != null && n < MIN_NIGHTS) {
      setMsg({ kind: "err", text: `Stays are ${MIN_NIGHTS}-${MAX_NIGHTS} nights. For shorter stays, contact us directly.` });
      setCheckin("");
      setCheckout("");
      return;
    }
    if (n != null && n > MAX_NIGHTS) {
      setMsg({ kind: "err", text: `Stays are limited to ${MAX_NIGHTS} nights. For longer stays, contact us directly.` });
      setCheckin("");
      setCheckout("");
      return;
    }

    setCheckin(newCheckin);
    setCheckout(newCheckout);
  }

  function submit(e) {
    e.preventDefault();
    setMsg(null);
    if (!checkin || !checkout) {
      setMsg({ kind: "err", text: "Pick check-in and check-out dates." });
      return;
    }
    const n = computeNights(checkin, checkout);
    if (n == null) {
      setMsg({ kind: "err", text: "Check-out must be after check-in." });
      return;
    }
    if (available === false) {
      setMsg({ kind: "err", text: "Those dates overlap an existing booking. Try different nights." });
      return;
    }
    onSelectDates?.({
      checkin,
      checkout,
      nights: n,
      pricing_band: data.pricing_band,
      rate_bands: data.rate_bands,
    });
  }

  // Generate the days for the current view month.
  const calendarDays = useMemo(() => {
    const { year, month } = viewMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];
    // Leading blanks.
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(new Date(year, month, d));
    }
    // Trailing blanks to fill the last week.
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [viewMonth]);

  function prevMonth() {
    setViewMonth((v) => {
      let m = v.month - 1;
      let y = v.year;
      if (m < 0) {
        m = 11;
        y--;
      }
      return { year: y, month: m };
    });
  }

  function nextMonth() {
    setViewMonth((v) => {
      let m = v.month + 1;
      let y = v.year;
      if (m > 11) {
        m = 0;
        y++;
      }
      return { year: y, month: m };
    });
  }

  // For hover preview of the range.
  const hoverRange = useMemo(() => {
    if (!checkin || checkout || !hoverDate) return null;
    const ci = parseISO(checkin).getTime();
    const ho = parseISO(hoverDate).getTime();
    if (ho <= ci) return null;
    // Check no overlap.
    if (!isRangeAvailable(data.booked, checkin, hoverDate)) return null;
    const hn = computeNights(checkin, hoverDate);
    if (hn == null || hn < MIN_NIGHTS) return null;
    return { start: ci, end: ho };
  }, [checkin, checkout, hoverDate, data.booked]);

  const canGoPrev = viewMonth.year > today.getFullYear() || (viewMonth.year === today.getFullYear() && viewMonth.month > today.getMonth());
  // 18-month booking horizon (MAX_ADVANCE_DAYS): the next-month button stops
  // once the following month starts entirely beyond the cap; days past the
  // cap inside the current month render disabled like past days.
  const canGoNext = !isBeyondAdvanceWindow(new Date(viewMonth.year, viewMonth.month + 1, 1), today);

  function fmtMoney(n) {
    return "$" + Math.round(n).toLocaleString("en-US");
  }

  return (
    <form class="surface" onSubmit={submit}>
      <h2>Check availability</h2>

      <div class="cal-header">
        <button type="button" class="cal-nav" onClick={prevMonth} disabled={!canGoPrev || !loaded} aria-label="Previous month">&lt;</button>
        <span class="cal-month">{MONTHS[viewMonth.month]} {viewMonth.year}</span>
        <button
          type="button"
          class="cal-refresh"
          onClick={refresh}
          disabled={refreshing || !loaded}
          title="Refresh availability"
          aria-label="Refresh availability"
        >
          {refreshing ? "…" : "↻"}
        </button>
        <button type="button" class="cal-nav" onClick={nextMonth} disabled={!loaded || !canGoNext} aria-label="Next month">&gt;</button>
      </div>

      <div class={"cal-grid" + (loaded ? "" : " cal-loading")}>
        {DOW.map((d) => <div class="cal-dow">{d}</div>)}
        {calendarDays.map((date, i) => {
          if (!date) return <div class="cal-cell cal-empty" key={i} />;
          const iso = toISO(date);
          const past = isPast(date, today);
          const beyond = isBeyondAdvanceWindow(date, today);
          const booked = isDateBooked(date, data.booked);
          const inRange = isInSelectedRange(date, checkin, checkout);
          const isCheckin = iso === checkin;
          const isCheckout = iso === checkout;
          const inHover = hoverRange && date.getTime() > hoverRange.start && date.getTime() <= hoverRange.end;
          const outOfRange = checkin && !checkout && iso !== checkin
            ? (() => {
                const n = computeNights(checkin, iso);
                return n != null && n < MIN_NIGHTS;
              })()
            : false;
          const disabled = !loaded || past || booked || beyond;
          const classes = [
            "cal-cell",
            past ? "cal-past" : "",
            booked ? "cal-booked" : "",
            inRange ? "cal-selected" : "",
            inHover ? "cal-hover" : "",
            isCheckin ? "cal-checkin" : "",
            isCheckout ? "cal-checkout" : "",
            disabled ? "cal-disabled" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              type="button"
              class={classes}
              key={iso}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => setHoverDate(iso)}
              onMouseLeave={() => setHoverDate("")}
              disabled={disabled}
              title={booked ? "Booked" : beyond ? "Too far ahead" : ""}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div class="cal-legend">
        <span class="cal-legend-item"><span class="cal-dot cal-dot-available" /> Available</span>
        <span class="cal-legend-item"><span class="cal-dot cal-dot-booked" /> Booked</span>
        <span class="cal-legend-item"><span class="cal-dot cal-dot-selected" /> Your selection</span>
      </div>

      {checkin && checkout ? (
        <div class="price-estimate">
          <p class="band">
            {checkin} to {checkout} — {nights} nights
            {available === false ? " (overlaps a booking)" : ""}
          </p>
          {available !== false && priceEst ? (
            <p class="price-total">
              Approximate {fmtMoney(priceEst.total)} for {priceEst.nights} nights
              <span class="price-detail"> · ~{fmtMoney(priceEst.perNight)}/night, before fees</span>
            </p>
          ) : null}
        </div>
      ) : checkin ? (
        <p class="band">Check-in: {checkin}. Now pick a check-out date.</p>
      ) : loadError ? (
        <div class="availability-error">
          <Status kind="err">{loadError}</Status>
          <p class="band">Dates stay locked until availability loads.</p>
          <button type="button" class="cal-retry" onClick={() => setLoadAttempt((a) => a + 1)}>
            Try again
          </button>
        </div>
      ) : (
        <p class="band">{loaded ? "Click a date to pick your check-in." : "Loading availability…"}</p>
      )}

      <button type="submit" disabled={!checkin || !checkout || (nights != null && !inNightRange(nights)) || available === false || !loaded}>
        Continue
      </button>
      <Status kind={msg?.kind === "err" ? "err" : msg?.kind === "warn" ? "warn" : "ok"}>{msg?.text}</Status>
      {loaded && stale ? <p class="band hint">Showing cached availability — tap ↻ to refresh.</p> : null}
    </form>
  );
}
